import { RP_STRIPE_SCRIPT_ATTR, STRIPE_JS_URL } from './constants';

let _stripeConstructorPromise = null;

/**
 * Loads Stripe.js (caching the promise across calls) and resolves to `window.Stripe`.
 *
 * Mirrors `loadStripeConstructor` in `checkout/client/lib/rp-payments/stripe-confirmation-token.js` so
 * cart and checkout pages share the same script-detection behavior. If another consumer (Spreedly, the
 * checkout SPA) already injected Stripe.js, we attach to that existing tag instead of injecting a
 * second one.
 *
 * @returns {Promise<object>} Resolves to the global Stripe constructor.
 */
export default function loadStripeConstructor() {
	if (typeof window === 'undefined') {
		return Promise.reject(new Error('Stripe.js requires a browser environment'));
	}
	if (window.Stripe) {
		return Promise.resolve(window.Stripe);
	}
	if (_stripeConstructorPromise) {
		return _stripeConstructorPromise;
	}

	_stripeConstructorPromise = new Promise((resolve, reject) => {
		let sharedScript = null;

		const onLoad = () => {
			if (sharedScript) {
				sharedScript.removeEventListener('load', onLoad);
				sharedScript.removeEventListener('error', onError);
			}
			if (window.Stripe) {
				resolve(window.Stripe);
			} else {
				reject(new Error('Stripe.js loaded but window.Stripe is not defined'));
			}
		};
		const onError = () => {
			if (sharedScript) {
				sharedScript.removeEventListener('load', onLoad);
				sharedScript.removeEventListener('error', onError);
			}
			reject(new Error('Failed to load Stripe.js'));
		};

		const ours = document.querySelector(`script[src*="js.stripe.com"][${RP_STRIPE_SCRIPT_ATTR}]`);
		if (ours) {
			ours.remove();
		}

		sharedScript = document.querySelector('script[src*="js.stripe.com"]');
		if (sharedScript) {
			sharedScript.addEventListener('load', onLoad);
			sharedScript.addEventListener('error', onError);
			// A tag may already have finished loading; the `load` event does not replay.
			queueMicrotask(() => {
				if (window.Stripe) {
					onLoad();
				}
			});
			return;
		}

		const script = document.createElement('script');
		script.src = STRIPE_JS_URL;
		script.async = true;
		// Match `<link rel="preload" ... crossorigin>` in page-head so the preload is consumed.
		script.crossOrigin = 'anonymous';
		script.setAttribute(RP_STRIPE_SCRIPT_ATTR, '');
		script.onload = onLoad;
		script.onerror = onError;
		document.head.appendChild(script);
	});

	_stripeConstructorPromise = _stripeConstructorPromise.catch((err) => {
		_stripeConstructorPromise = null;
		throw err;
	});
	return _stripeConstructorPromise;
}
